// The story on the page: sentences in rows, each word a button, each
// unresolved word a slot that plays the child's voice and never shows text.
// Between rows, a gap the child taps to say the next part there. The margin
// number is the sentence's handle: tap to pick the sentence up for its tools,
// drag to move it. Nothing here decides anything; every tap calls a handler.
import {
  type CompositionToken,
  sentences,
  type UnresolvedToken,
  type WordToken,
} from "@bindery/composition";
import { ArrowDown, ArrowUp, Trash2, Volume2 } from "lucide-react";
import { type RefObject, useId, useRef, useState } from "react";

export type WordMark = "doubt" | "new";

// The marks are dots drawn in CSS; this is what they say to a screen reader.
const MARK_DESCRIPTION: Readonly<Record<WordMark, string>> = {
  doubt: "Let's check this word",
  new: "A new word to hear",
};

export interface StoryProps {
  readonly tokens: readonly CompositionToken[];
  readonly marks: ReadonlyMap<string, WordMark>;
  /** The word being read aloud right now. */
  readonly lit: string | null;
  /** The word whose sheet is open. */
  readonly open: string | null;
  readonly cursor: number;
  readonly selected: number | null;
  readonly hasClip: (token: WordToken | UnresolvedToken) => boolean;
  readonly onTapWord: (token: WordToken) => void;
  readonly onTapSlot: (token: UnresolvedToken) => void;
  readonly onTapGap: (sentence: number) => void;
  readonly onSelectSentence: (sentence: number | null) => void;
  readonly onHearSentence: (sentence: number) => void;
  readonly onMoveSentence: (from: number, to: number) => void;
  readonly onDeleteSentence: (sentence: number) => void;
}

interface Drag {
  readonly from: number;
  /** Insertion slot among the rows, 0..n. */
  readonly slot: number;
}

/** The sentence index a row dropped at `slot` ends up at. */
export const dropTarget = (from: number, slot: number): number =>
  slot > from ? slot - 1 : slot;

function slotAt(container: HTMLElement, y: number): number {
  const rows = [...container.querySelectorAll<HTMLElement>("[data-sentence]")];
  return rows.filter((row) => {
    const rect = row.getBoundingClientRect();
    return rect.top + rect.height / 2 < y;
  }).length;
}

// Pointer-driven so it works on the touch devices K–2 use; HTML drag and
// drop does not fire on iOS Safari (from memory, unverified).
function useSentenceDrag(
  container: RefObject<HTMLElement | null>,
  onMove: (from: number, to: number) => void,
) {
  const [drag, setDrag] = useState<Drag | null>(null);
  const dragRef = useRef<Drag | null>(null);

  const begin = (from: number) => (event: React.PointerEvent) => {
    if (event.button !== 0) return;
    const start: Drag = { from, slot: from };
    dragRef.current = start;
    setDrag(start);
    const move = (e: PointerEvent) => {
      const root = container.current;
      const current = dragRef.current;
      if (root === null || current === null) return;
      const next = { ...current, slot: slotAt(root, e.clientY) };
      if (next.slot !== current.slot) {
        dragRef.current = next;
        setDrag(next);
      }
    };
    const end = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", end);
      window.removeEventListener("pointercancel", end);
      const current = dragRef.current;
      dragRef.current = null;
      setDrag(null);
      if (current === null) return;
      const to = dropTarget(current.from, current.slot);
      if (to !== current.from) onMove(current.from, to);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", end);
    window.addEventListener("pointercancel", end);
  };

  return { drag, begin };
}

const tokenMap = (tokens: readonly CompositionToken[]) =>
  new Map(tokens.map((t) => [t.id, t] as const));

function Gap({
  index,
  active,
  onTap,
}: {
  index: number;
  active: boolean;
  onTap: (index: number) => void;
}) {
  return (
    <button
      type="button"
      className="compose-gap"
      data-cursor={active ? "true" : undefined}
      aria-label={
        active ? "Your next words go here" : "Put the next words here"
      }
      aria-pressed={active}
      onClick={() => onTap(index)}
    >
      ·
    </button>
  );
}

function SentenceTools({
  index,
  count,
  onHear,
  onMove,
  onDelete,
}: {
  index: number;
  count: number;
  onHear: () => void;
  onMove: (to: number) => void;
  onDelete: () => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-2 pt-1 pl-14 leading-none">
      <button
        type="button"
        className="compose-icon"
        data-tone="hear"
        aria-label="Hear this sentence"
        onClick={onHear}
      >
        <Volume2 className="size-7" />
      </button>
      <button
        type="button"
        className="compose-icon"
        aria-label="Move this sentence up"
        disabled={index === 0}
        onClick={() => onMove(index - 1)}
      >
        <ArrowUp className="size-7" />
      </button>
      <button
        type="button"
        className="compose-icon"
        aria-label="Move this sentence down"
        disabled={index >= count - 1}
        onClick={() => onMove(index + 1)}
      >
        <ArrowDown className="size-7" />
      </button>
      <button
        type="button"
        className="compose-icon ml-auto"
        data-tone="away"
        aria-label="Take this sentence out"
        onClick={onDelete}
      >
        <Trash2 className="size-7" />
      </button>
    </div>
  );
}

export function Story(props: StoryProps) {
  const container = useRef<HTMLDivElement | null>(null);
  const { drag, begin } = useSentenceDrag(container, props.onMoveSentence);
  const byId = tokenMap(props.tokens);
  const groups = sentences(props.tokens);
  const marksId = useId();
  const markId = (mark: WordMark) => `${marksId}-${mark}`;

  const dropMark = (index: number): "before" | "after" | undefined => {
    if (drag === null || drag.from === index) return undefined;
    if (drag.slot === index) return "before";
    if (drag.slot === index + 1 && index === groups.length - 1) return "after";
    return undefined;
  };

  return (
    <div ref={container} className="flex flex-col">
      {(["doubt", "new"] as const).map((mark) => (
        <span key={mark} id={markId(mark)} hidden>
          {MARK_DESCRIPTION[mark]}
        </span>
      ))}
      {groups.map((ids, index) => (
        <div key={ids[0] ?? index}>
          <Gap
            index={index}
            active={props.cursor === index}
            onTap={props.onTapGap}
          />
          <div
            data-sentence={index}
            data-dragging={drag?.from === index ? "true" : undefined}
            data-drop={dropMark(index)}
            className="compose-sentence rounded-xl"
          >
            <div className="flex items-start gap-2">
              <button
                type="button"
                className="compose-handle"
                data-selected={props.selected === index ? "true" : undefined}
                aria-label={`Sentence ${String(index + 1)}: pick up or move`}
                aria-pressed={props.selected === index}
                onPointerDown={begin(index)}
                onClick={() =>
                  props.onSelectSentence(
                    props.selected === index ? null : index,
                  )
                }
              >
                {index + 1}
              </button>
              <p className="m-0 flex flex-1 flex-wrap items-end gap-x-0.5">
                {ids.map((id) => {
                  const token = byId.get(id);
                  if (token === undefined) return null;
                  switch (token.kind) {
                    case "word": {
                      const mark = props.marks.get(id);
                      return (
                        <button
                          key={id}
                          type="button"
                          className="compose-word"
                          data-lit={props.lit === id ? "true" : undefined}
                          data-open={props.open === id ? "true" : undefined}
                          data-mark={mark}
                          aria-describedby={
                            mark === undefined ? undefined : markId(mark)
                          }
                          onClick={() => props.onTapWord(token)}
                        >
                          {token.text}
                        </button>
                      );
                    }
                    case "punct":
                      return (
                        <span key={id} className="compose-punct" aria-hidden>
                          {token.text}
                        </span>
                      );
                    case "unresolved": {
                      const sound = props.hasClip(token);
                      return (
                        <button
                          key={id}
                          type="button"
                          className="compose-slot"
                          data-silent={sound ? undefined : "true"}
                          aria-label={
                            sound
                              ? "A word for a grown-up: hear what you said"
                              : "A word for a grown-up"
                          }
                          onClick={() => props.onTapSlot(token)}
                        >
                          <Volume2 className="size-7" />
                        </button>
                      );
                    }
                    default:
                      return null;
                  }
                })}
              </p>
            </div>
            {props.selected === index ? (
              <SentenceTools
                index={index}
                count={groups.length}
                onHear={() => props.onHearSentence(index)}
                onMove={(to) => props.onMoveSentence(index, to)}
                onDelete={() => props.onDeleteSentence(index)}
              />
            ) : null}
          </div>
        </div>
      ))}
      <Gap
        index={groups.length}
        active={props.cursor >= groups.length}
        onTap={props.onTapGap}
      />
    </div>
  );
}
